import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import { keysToCamelCase } from '@shared/utils/utils';

const testRunsSlice = createSlice({
    name: '@testRuns',
    initialState: {
        testRuns: [],
        testRunsCount: 0,
        testResults: [],
        testResultsCount: 0,
        testRun: null,
        testRunTimeline: [],
        testRunTimelineCount: 0,
        testResult: null,
        testResultsTimeline: [],
        testResultsTimelineCount: 0,
        testResultsByTestcase: [],
        getTestResultsByTestcaseError: null,
        getTestResultsByTestcaseStatus: REQUEST_STATUS.NOT_REQUESTED,
        getStatus: REQUEST_STATUS.NOT_REQUESTED,
        getError: null,
        getTestResultsStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestResultsError: null,
        getTestRunTimelineStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestRunTimelineError: null,
        getTestRunByIdStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestRunByIdError: null,
        editTestRunResultByIdStatus: REQUEST_STATUS.NOT_REQUESTED,
        editTestRunResultByIdError: null,
        actionWithTestrunStatus: REQUEST_STATUS.NOT_REQUESTED,
        actionWithTestrunError: null,
        patchTestRunStatus: REQUEST_STATUS.NOT_REQUESTED,
        patchTestRunError: null,
        patchTestRunResultsStatus: REQUEST_STATUS.NOT_REQUESTED,
        patchTestRunResultsError: null,
        patchTestResultStatus: REQUEST_STATUS.NOT_REQUESTED,
        patchTestResultError: null,
        getTestResultByIdStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestResultByIdError: null,
        getTestResultsTimelineStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestResultsTimelineError: null,
        testResultsBuilds: [],
        testResultsBranches: [],
        getTestResultsBuildsStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestResultsBuildsError: null,
        getTestResultsBranchesStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestResultsBranchesError: null,
        testRunsBuilds: [],
        testRunsBranches: [],
        testRunsBuildsStatus: REQUEST_STATUS.NOT_REQUESTED,
        testRunsBuildsError: null,
        testRunsBranchesStatus: REQUEST_STATUS.NOT_REQUESTED,
        testRunsBranchesError: null,
        matchedTestResults: [],
        matchedTestResultsCount: 0,
        getMatchedTestResultsStatus: REQUEST_STATUS.NOT_REQUESTED,
        getMatchedTestResultsError: null,
        matchedTestResult: null,
        getMatchedTestResultByIdStatus: REQUEST_STATUS.NOT_REQUESTED,
        getMatchedTestResultByIdError: null,
    },

    reducers: {
        getTestRunsRequest(state) {
            state.getStatus = REQUEST_STATUS.LOADING;
            state.getError = null;
        },
        getTestRunsSuccess(state, action) {
            state.getStatus = REQUEST_STATUS.SUCCESS;
            state.getError = null;
            state.testRuns = action.payload.data.items;
            state.testRunsCount = action.payload.data.count;
        },
        getTestRunsError(state, action) {
            state.getStatus = REQUEST_STATUS.ERROR;
            state.getError = action;
            state.testRuns = [];
            state.testRunsCount = 0;
        },
        getTestRunsReset(state) {
            state.getStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getError = null;
            state.testRuns = [];
            state.testRunsCount = 0;
        },
        getTestRunsTimelineRequest(state) {
            state.getTestRunTimelineStatus = REQUEST_STATUS.LOADING;
            state.getTestRunTimelineError = null;
        },
        getTestRunsTimelineSuccess(state, action) {
            state.getTestRunTimelineStatus = REQUEST_STATUS.SUCCESS;
            state.getTestRunTimelineError = null;
            state.testRunTimeline.push(...action.payload.data.items);
            state.testRunTimelineCount = action.payload.data.count;
        },
        getTestRunsTimelineError(state, action) {
            state.getTestRunTimelineStatus = REQUEST_STATUS.ERROR;
            state.getTestRunTimelineError = action;
            state.testRunTimeline = [];
            state.testRunTimelineCount = 0;
        },
        getTestRunsTimelineReset(state) {
            state.getTestRunTimelineStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestRunTimelineError = null;
            state.testRunTimeline = [];
            state.testRunTimelineCount = 0;
        },

        getTestRunByIdRequest(state) {
            state.getTestRunByIdStatus = REQUEST_STATUS.LOADING;
            state.getTestRunByIdError = null;
        },
        getTestRunByIdSuccess(state, action) {
            state.getTestRunByIdStatus = REQUEST_STATUS.SUCCESS;
            state.getTestRunByIdError = null;
            state.testRun = structuredClone(action.payload.data);
        },
        getTestRunByIdError(state, action) {
            state.getTestRunByIdStatus = REQUEST_STATUS.ERROR;
            state.getTestRunByIdError = action;
            state.testRun = null;
        },
        getTestRunByIdReset(state) {
            state.getTestRunByIdStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestRunByIdError = null;
            state.testRun = null;
        },

        actionWithTestrunRequest(state) {
            state.actionWithTestrunStatus = REQUEST_STATUS.LOADING;
            state.actionWithTestrunError = null;
        },
        actionWithTestrunSuccess(state) {
            state.actionWithTestrunStatus = REQUEST_STATUS.SUCCESS;
            state.actionWithTestrunError = null;
        },
        actionWithTestrunError(state, action) {
            state.actionWithTestrunStatus = REQUEST_STATUS.ERROR;
            state.actionWithTestrunError = action;
        },
        actionWithTestrunReset(state) {
            state.actionWithTestrunStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.actionWithTestrunError = null;
        },

        // READ
        getTestResultsRequest(state) {
            state.getTestResultsStatus = REQUEST_STATUS.LOADING;
            state.getTestResultsError = null;
            state.testResults = [];
        },
        getTestResultsSuccess(state, action) {
            state.getTestResultsStatus = REQUEST_STATUS.SUCCESS;
            state.getTestResultsError = null;
            state.testResults = action.payload.data.items.map((testresult) => ({
                ...testresult,
                defects: testresult.defects.map((defect) => keysToCamelCase(defect)),
            }));
            state.testResultsCount = action.payload.data.count;
        },
        getTestResultsError(state, action) {
            state.getTestResultsStatus = REQUEST_STATUS.ERROR;
            state.getTestResultsError = action;
            state.testResults = [];
            state.testResultsCount = 0;
        },
        getTestResultsReset(state) {
            state.getTestResultsStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestResultsError = null;
            state.testResults = [];
            state.testResultsCount = 0;
        },

        patchTestRunRequest(state) {
            state.patchTestRunStatus = REQUEST_STATUS.LOADING;
            state.patchTestRunError = null;
        },
        patchTestRunSuccess(state) {
            state.patchTestRunStatus = REQUEST_STATUS.SUCCESS;
            state.patchTestRunError = null;
        },
        patchTestRunError(state, action) {
            state.patchTestRunStatus = REQUEST_STATUS.ERROR;
            state.patchTestRunError = action;
        },
        patchTestRunReset(state) {
            state.patchTestRunStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.patchTestRunError = null;
        },

        patchTestRunResultsRequest(state) {
            state.patchTestRunResultsStatus = REQUEST_STATUS.LOADING;
            state.patchTestRunResultsError = null;
        },
        patchTestRunResultsSuccess(state, action) {
            state.patchTestRunResultsStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.patchTestRunResultsError = null;
            const updatedItems = action.payload.items;
            const map = new Map(state.testResults.map((item) => [item.pk, item]));
            updatedItems.forEach((item) => {
                const existing = map.get(item.pk) || {};
                map.set(item.pk, {
                    ...existing,
                    ...item,
                });
            });
            state.testResults = Array.from(map.values());
        },
        patchTestRunResultsError(state, action) {
            state.patchTestRunResultsStatus = REQUEST_STATUS.ERROR;
            state.patchTestRunResultsError = action;
        },
        patchTestRunResultsReset(state) {
            state.patchTestRunResultsStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.patchTestRunResultsError = null;
        },

        patchTestResultRequest(state) {
            state.patchTestResultStatus = REQUEST_STATUS.LOADING;
            state.patchTestResultError = null;
        },
        patchTestResultSuccess(state) {
            state.patchTestResultStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.patchTestResultError = null;
        },
        patchTestResultError(state, action) {
            state.patchTestResultStatus = REQUEST_STATUS.ERROR;
            state.patchTestResultError = action;
        },
        patchTestResultReset(state) {
            state.patchTestResultStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.patchTestResultError = null;
        },

        getTestResultByIdRequest(state) {
            state.getTestResultByIdStatus = REQUEST_STATUS.LOADING;
            state.getTestResultByIdError = null;
        },
        getTestResultByIdSuccess(state, action) {
            state.getTestResultByIdStatus = REQUEST_STATUS.SUCCESS;
            state.getTestResultByIdError = null;
            state.testResult = {
                ...action.payload.data,
                defects: action.payload.data.defects.map((defect) => keysToCamelCase(defect)),
            };
        },
        getTestResultByIdError(state, action) {
            state.getTestResultByIdStatus = REQUEST_STATUS.ERROR;
            state.getTestResultByIdError = action;
            state.testResult = null;
        },
        getTestResultByIdReset(state) {
            state.getTestResultByIdStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestResultByIdError = null;
            state.testResult = null;
        },

        getTestResultsTimelineRequest(state) {
            state.getTestResultsTimelineStatus = REQUEST_STATUS.LOADING;
            state.getTestResultsTimelineError = null;
        },
        getTestResultsTimelineSuccess(state, action) {
            state.getTestResultsTimelineStatus = REQUEST_STATUS.SUCCESS;
            state.getTestResultsTimelineError = null;
            state.testResultsTimeline.push(...action.payload.data.items);
            state.testResultsTimelineCount = action.payload.data.count;
        },
        getTestResultsTimelineError(state, action) {
            state.getTestResultsTimelineStatus = REQUEST_STATUS.ERROR;
            state.getTestResultsTimelineError = action;
            state.testResultsTimeline = [];
            state.testResultsTimelineCount = 0;
        },
        getTestResultsTimelineReset(state) {
            state.getTestResultsTimelineStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestResultsTimelineError = null;
            state.testResultsTimeline = [];
            state.testResultsTimelineCount = 0;
        },

        // FILTERS

        getTestRunsBranchesFilterRequest(state) {
            state.getTestRunsBranchesStatus = REQUEST_STATUS.LOADING;
            state.getTestRunsBranchesError = null;
        },
        getTestRunsBranchesFilterSuccess(state, action) {
            state.getTestRunsBranchesStatus = REQUEST_STATUS.SUCCESS;
            state.getTestRunsBranchesError = null;
            state.testRunsBranches = action.payload.data;
        },
        getTestRunsBranchesFilterError(state, action) {
            state.getTestRunsBranchesStatus = REQUEST_STATUS.ERROR;
            state.getTestRunsBranchesError = action;
            state.testRunsBranches = [];
        },
        getTestRunsBranchesFilterReset(state) {
            state.getTestRunsBranchesStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestRunsBranchesError = null;
            state.testRunsBranches = [];
        },

        getTestRunsBuildsFilterRequest(state) {
            state.getTestRunsBuildsStatus = REQUEST_STATUS.LOADING;
            state.getTestRunsBuildsError = null;
        },
        getTestRunsBuildsFilterSuccess(state, action) {
            state.getTestRunsBuildsStatus = REQUEST_STATUS.SUCCESS;
            state.getTestRunsBuildsError = null;
            state.testRunsBuilds = action.payload.data;
        },
        getTestRunsBuildsFilterError(state, action) {
            state.getTestRunsBuildsStatus = REQUEST_STATUS.ERROR;
            state.getTestRunsBuildsError = action;
            state.testRunsBuilds = [];
        },
        getTestRunsBuildsFilterReset(state) {
            state.getTestRunsBuildsStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestRunsBuildsError = null;
            state.testRunsBuilds = [];
        },

        getTestResultsBranchesFilterRequest(state) {
            state.getTestResultsBranchesStatus = REQUEST_STATUS.LOADING;
            state.getTestResultsBranchesError = null;
        },
        getTestResultsBranchesFilterSuccess(state, action) {
            state.getTestResultsBranchesStatus = REQUEST_STATUS.SUCCESS;
            state.getTestResultsBranchesError = null;
            state.testResultsBranches = action.payload.data;
        },
        getTestResultsBranchesFilterError(state, action) {
            state.getTestResultsBranchesStatus = REQUEST_STATUS.ERROR;
            state.getTestResultsBranchesError = action;
            state.testResultsBranches = [];
        },
        getTestResultsBranchesFilterReset(state) {
            state.getTestResultsBranchesStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestResultsBranchesError = null;
            state.testResultsBranches = [];
        },

        getTestResultsBuildsFilterRequest(state) {
            state.getTestResultsBuildsStatus = REQUEST_STATUS.LOADING;
            state.getTestResultsBuildsError = null;
        },
        getTestResultsBuildsFilterSuccess(state, action) {
            state.getTestResultsBuildsStatus = REQUEST_STATUS.SUCCESS;
            state.getTestResultsBuildsError = null;
            state.testResultsBuilds = action.payload.data;
        },
        getTestResultsBuildsFilterError(state, action) {
            state.getTestResultsBuildsStatus = REQUEST_STATUS.ERROR;
            state.getTestResultsBuildsError = action;
            state.testResultsBuilds = [];
        },
        getTestResultsBuildsFilterReset(state) {
            state.getTestResultsBuildsStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestResultsBuildsError = null;
            state.testResultsBuilds = [];
        },

        getTestResultsByTestcaseRequest(state) {
            state.getTestResultsByTestcaseStatus = REQUEST_STATUS.LOADING;
            state.getTestResultsByTestcaseError = null;
            state.testResultsByTestcase = [];
        },
        getTestResultsByTestcaseSuccess(state, action) {
            state.getTestResultsByTestcaseStatus = REQUEST_STATUS.SUCCESS;
            state.getTestResultsByTestcaseError = null;
            state.testResultsByTestcase = action.payload.data.items;
        },
        getTestResultsByTestcaseError(state, action) {
            state.getTestResultsByTestcaseStatus = REQUEST_STATUS.ERROR;
            state.getTestResultsByTestcaseError = action;
            state.testResultsByTestcase = [];
        },
        getTestResultsByTestcaseReset(state) {
            state.getTestResultsByTestcaseStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestResultsByTestcaseError = null;
            state.testResultsByTestcase = [];
        },
        getMatchedTestResultsRequest(state) {
            state.getMatchedTestResultsStatus = REQUEST_STATUS.LOADING;
            state.getMatchedTestResultsError = null;
            state.matchedTestResults = [];
        },
        getMatchedTestResultsSuccess(state, action) {
            state.getMatchedTestResultsStatus = REQUEST_STATUS.SUCCESS;
            state.matchedTestResults = action.payload.data.items;
            state.matchedTestResultsCount = action.payload.data.count;
        },
        getMatchedTestResultsError(state, action) {
            state.getMatchedTestResultsStatus = REQUEST_STATUS.ERROR;
            state.getMatchedTestResultsError = action;
            state.matchedTestResults = [];
            state.matchedTestResultsCount = 0;
        },
        getMatchedTestResultsReset(state) {
            state.getMatchedTestResultsStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getMatchedTestResultsError = null;
            state.matchedTestResults = [];
            state.matchedTestResultsCount = 0;
        },

        getMatchedTestResultByIdRequest(state) {
            state.getMatchedTestResultByIdStatus = REQUEST_STATUS.LOADING;
            state.getMatchedTestResultByIdError = null;
        },
        getMatchedTestResultByIdSuccess(state, action) {
            state.getMatchedTestResultByIdStatus = REQUEST_STATUS.SUCCESS;
            state.matchedTestResult = action.payload.data;
        },
        getMatchedTestResultByIdError(state, action) {
            state.getMatchedTestResultByIdStatus = REQUEST_STATUS.ERROR;
            state.getMatchedTestResultByIdError = action;
            state.matchedTestResult = null;
        },
        getMatchedTestResultByIdReset(state) {
            state.getMatchedTestResultByIdStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getMatchedTestResultByIdError = null;
            state.matchedTestResult = null;
        },
    },
});

export default testRunsSlice.reducer;

export const {
    getTestRunsRequest,
    getTestRunsSuccess,
    getTestRunsError,
    getTestRunsReset,
    getTestRunsTimelineRequest,
    getTestRunsTimelineSuccess,
    getTestRunsTimelineError,
    getTestRunsTimelineReset,
    getTestRunByIdRequest,
    getTestRunByIdSuccess,
    getTestRunByIdError,
    getTestRunByIdReset,
    actionWithTestrunRequest,
    actionWithTestrunSuccess,
    actionWithTestrunError,
    actionWithTestrunReset,
    getTestResultsRequest,
    getTestResultsSuccess,
    getTestResultsError,
    getTestResultsReset,
    patchTestRunRequest,
    patchTestRunSuccess,
    patchTestRunError,
    patchTestRunReset,
    patchTestRunResultsRequest,
    patchTestRunResultsSuccess,
    patchTestRunResultsError,
    patchTestRunResultsReset,
    patchTestResultRequest,
    patchTestResultSuccess,
    patchTestResultError,
    patchTestResultReset,
    getTestResultByIdRequest,
    getTestResultByIdSuccess,
    getTestResultByIdError,
    getTestResultByIdReset,
    getTestResultsTimelineRequest,
    getTestResultsTimelineSuccess,
    getTestResultsTimelineError,
    getTestResultsTimelineReset,
    getTestResultsBranchesFilterRequest,
    getTestResultsBranchesFilterSuccess,
    getTestResultsBranchesFilterError,
    getTestResultsBranchesFilterReset,
    getTestResultsBuildsFilterRequest,
    getTestResultsBuildsFilterError,
    getTestResultsBuildsFilterSuccess,
    getTestResultsBuildsFilterReset,
    getTestResultsByTestcaseRequest,
    getTestResultsByTestcaseReset,
    getTestResultsByTestcaseError,
    getTestResultsByTestcaseSuccess,
    getMatchedTestResultsRequest,
    getMatchedTestResultsSuccess,
    getMatchedTestResultsError,
    getMatchedTestResultsReset,
    getMatchedTestResultByIdRequest,
    getMatchedTestResultByIdSuccess,
    getMatchedTestResultByIdError,
    getMatchedTestResultByIdReset,
    getTestRunsBranchesFilterRequest,
    getTestRunsBranchesFilterError,
    getTestRunsBranchesFilterReset,
    getTestRunsBranchesFilterSuccess,
    getTestRunsBuildsFilterRequest,
    getTestRunsBuildsFilterError,
    getTestRunsBuildsFilterReset,
    getTestRunsBuildsFilterSuccess,
} = testRunsSlice.actions;
